import LiveInputBroadcaster from '@app/application/reconciliation_proposal/handlers/live-input-broadcaster'
import AnnotationService from '@app/application/reconciliation_proposal/services/annotation-service'
import MultiDocumentHandler from '@app/application/reconciliation_proposal/handlers/multi-document-handler'
import { SfLog } from '@app/adapters/logger'

export default class Mediator {
  constructor(pdfTronInstance, multipleDocuments) {
    this.pdfTronInstance = pdfTronInstance
    this.liveInputBroadcasters = {}
    this.liveInputUnsubscribers = {}
    this.liveInputFocused = false
    this.multipleDocuments = multipleDocuments
    this.onDocumentLoaded = this.onDocumentLoaded.bind(this)
    this.subscribeToEvents = this.subscribeToEvents.bind(this)
    this.buildLiveInputBroadcasters = this.buildLiveInputBroadcasters.bind(this)
    this.createAnnotations = this.createAnnotations.bind(this)
    this.lowlightAnnotation = this.lowlightAnnotation.bind(this)
    this.highlightAnnotation = this.highlightAnnotation.bind(this)
    this.focusAnnotation = this.focusAnnotation.bind(this)
    this.blurAnnotation = this.blurAnnotation.bind(this)
    this.mouseoverAnnotation = this.mouseoverAnnotation.bind(this)
    this.mouseoutAnnotation = this.mouseoutAnnotation.bind(this)
    this.runPrevTabEvent = this.runPrevTabEvent.bind(this)
    this.restart = this.restart.bind(this)

    const annotationManager = pdfTronInstance.Core.annotationManager
    const documentViewer = pdfTronInstance.Core.documentViewer
    annotationManager.instance = pdfTronInstance

    this.annotationService = new AnnotationService(
      annotationManager,
      documentViewer
    )
    if (this.multipleDocuments) {
      this.multiDocumentHandler = new MultiDocumentHandler(
        pdfTronInstance.UI,
        this.annotationService
      )
    }
  }

  start() {
    this.annotationService.documentViewer.addEventListener(
      'documentLoaded',
      this.onDocumentLoaded
    )

    this.annotationService.documentViewer.addEventListener(
      'pageComplete',
      this.runPrevTabEvent
    )

    if (this.multiDocumentHandler) {
      this.multiDocumentHandler.start()
    }

    window.eventHub.$on('sf-live-inputs:savedChanges', this.restart)
  }

  runPrevTabEvent(pageNumber) {
    if (this.multipleDocuments) {
      const targetCoordinates =
        this.multiDocumentHandler.targetFromPrevTab[pageNumber]
      if (targetCoordinates) {
        this.highlightAnnotation(targetCoordinates)
        this.multiDocumentHandler.targetFromPrevTab = {}
      }
    }
  }

  restart() {
    this.stopBroadcasters()
    if (this.multiDocumentHandler) {
      this.multiDocumentHandler.cleanUpAnnotations()
    }
    this.onDocumentLoaded()
  }

  async onDocumentLoaded() {
    try {
      // fit to width on each tab load
      this.pdfTronInstance.UI.setFitMode(
        this.pdfTronInstance.UI.FitMode.FitWidth
      )
      this.buildLiveInputBroadcasters()
      await this.createAnnotations()
      this.subscribeToEvents()
    } catch (e) {
      SfLog.error('document viewer load error', { error: e })
    }
  }

  createAnnotations() {
    Object.values(this.liveInputBroadcasters).forEach((broadcaster) => {
      // do not draw annotation if it already exists
      const coordinates =
        broadcaster.targetElement.dataset.aiProposalCoordinates

      const onTheCurrentTab =
        !this.multipleDocuments ||
        this.multiDocumentHandler.onTheCurrentTab(
          broadcaster.targetElement.dataset.aiProposalFilename
        )
      const existingAnnotation = this.annotationService.annotations[coordinates]
      if (existingAnnotation) {
        return
      }

      if (onTheCurrentTab) {
        const annotation = this.annotationService.buildAnnotation(
          coordinates,
          broadcaster.targetElement.dataset.aiProposalPage
        )

        this.annotationService.annotations[coordinates] = annotation
      }
    })

    return this.annotationService.drawAnnotations(
      Object.values(this.annotationService.annotations)
    )
  }

  subscribeToEvents() {
    Object.values(this.liveInputBroadcasters).forEach((broadcaster) => {
      // do not subscribe if already subscribed
      if (this.liveInputUnsubscribers[broadcaster.targetSelector]) {
        return
      }

      const filename = broadcaster.targetElement.dataset.aiProposalFilename
      const pageNumber =
        parseInt(broadcaster.targetElement.dataset.aiProposalPage) + 1
      const coordinates =
        broadcaster.targetElement.dataset.aiProposalCoordinates
      const onFocus = this.focusAnnotation(filename, pageNumber, coordinates)
      const onBlur = this.blurAnnotation(filename, coordinates)
      const onMouseOver = this.mouseoverAnnotation(
        filename,
        pageNumber,
        coordinates
      )
      const onMouseOut = this.mouseoutAnnotation(filename, coordinates)

      window.eventHub.$on(
        `aiProposedInput:focus:${broadcaster.targetSelector}`,
        onFocus
      )
      window.eventHub.$on(
        `aiProposedInput:blur:${broadcaster.targetSelector}`,
        onBlur
      )
      window.eventHub.$on(
        `aiProposedInput:mouseover:${broadcaster.targetSelector}`,
        onMouseOver
      )
      window.eventHub.$on(
        `aiProposedInput:mouseout:${broadcaster.targetSelector}`,
        onMouseOut
      )

      this.liveInputUnsubscribers[broadcaster.targetSelector] = () => {
        window.eventHub.$off(
          `aiProposedInput:focus:${broadcaster.targetSelector}`,
          onFocus
        )
        window.eventHub.$off(
          `aiProposedInput:blur:${broadcaster.targetSelector}`,
          onBlur
        )
        window.eventHub.$off(
          `aiProposedInput:mouseover:${broadcaster.targetSelector}`,
          onMouseOver
        )
        window.eventHub.$off(
          `aiProposedInput:mouseout:${broadcaster.targetSelector}`,
          onMouseOut
        )
      }
    })
  }

  focusAnnotation(filename, pageNumber, targetCoordinates) {
    return () => {
      this.liveInputFocused = true
      if (this.multipleDocuments) {
        const tabSwitched = this.multiDocumentHandler.switchTabTo(
          filename,
          pageNumber,
          targetCoordinates
        )
        if (!tabSwitched) {
          this.highlightAnnotation(targetCoordinates)
        }
      } else {
        this.highlightAnnotation(targetCoordinates)
      }
    }
  }

  blurAnnotation(filename, targetCoordinates) {
    return () => {
      this.liveInputFocused = false
      this.lowlightAnnotation(targetCoordinates)
    }
  }

  mouseoverAnnotation(filename, pageNumber, targetCoordinates) {
    return () => {
      if (this.liveInputFocused) {
        return
      }
      if (this.multipleDocuments) {
        const tabSwitched = this.multiDocumentHandler.switchTabTo(
          filename,
          pageNumber,
          targetCoordinates
        )
        if (!tabSwitched) {
          this.highlightAnnotation(targetCoordinates)
        }
      } else {
        this.highlightAnnotation(targetCoordinates)
      }
    }
  }

  mouseoutAnnotation(filename, targetCoordinates) {
    return () => {
      if (this.liveInputFocused) {
        return
      }
      this.lowlightAnnotation(targetCoordinates)
    }
  }

  highlightAnnotation(targetCoordinates) {
    const annotation = this.annotationService.annotations[targetCoordinates]
    if (!annotation) {
      return
    }

    this.annotationService.highlightAnnotation(annotation)
    this.annotationService.jumpToAnnotation(annotation)
  }

  lowlightAnnotation(targetCoordinates) {
    const annotation = this.annotationService.annotations[targetCoordinates]
    if (!annotation) {
      return
    }

    this.annotationService.lowlightAnnotation(annotation)
  }

  buildLiveInputBroadcasters() {
    const elements = document.querySelectorAll('[data-ai-proposal-coordinates]')

    elements.forEach((element) => {
      if (this.liveInputBroadcasters[element.dataset.name]) {
        return
      }
      const broadcaster = new LiveInputBroadcaster(element.dataset.name)
      this.liveInputBroadcasters[element.dataset.name] = broadcaster
    })
  }

  stop() {
    this.stopBroadcasters()

    this.annotationService.documentViewer.removeEventListener(
      'documentLoaded',
      this.onDocumentLoaded
    )
    this.annotationService.documentViewer.removeEventListener(
      'pageComplete',
      this.runPrevTabEvent
    )
    this.annotationService.deleteAnnotations()
    if (this.multiDocumentHandler) {
      this.multiDocumentHandler.teardown()
    }

    window.eventHub.$off('sf-live-inputs:savedChanges', this.restart)
  }

  stopBroadcasters() {
    Object.values(this.liveInputBroadcasters).forEach((broadcaster) => {
      broadcaster.teardown()
    })
    Object.values(this.liveInputUnsubscribers).forEach((unsubscriber) => {
      unsubscriber()
    })
    this.liveInputBroadcasters = {}
    this.liveInputUnsubscribers = {}
  }
}
